// @flow
import cn from 'classnames';
import React, { Component } from 'react';
import { connect } from 'react-redux';

import type { FooterBlock } from '#common/flow-types/blocks';
import type { IRenderMode } from '#common/flow-types/build';
import type { ImageValue } from '#common/flow-types/values';
import type { Asset } from '#common/model/asset';
import type { WidgetsType } from '#common/model/landing';
import type { FiveSymbolLocale } from '#common/types/locale'
import type { Landing } from '#common/types/model/landing';
import type { RootState } from '../../../reducers/modules/site-builder';
import type { setAssetType } from '../../../utils/types';

import { setAssets } from '../../../reducers/modules/site-builder/assets';
import {
  changeBlockById, checkoutBlock, saveBlockProcess, changeValue
} from '../../../reducers/modules/site-builder/blocks';
import { selectMerchantId } from '../../../reducers/modules/site-builder/landing';
import AffiliateButton from '../../../ui-components/Editor/AffiliateButton';
import Button from '../../../ui-components/Editor/Button';
import { EditorText } from '../../../ui-components/Editor/EditorText';
import { LocaleSelect, LocaleSelectType } from '../../../ui-components/Editor/LocaleSelect';
import { Media } from '../../../ui-components/Editor/Media';
import PrivacySettingsButton from '../../../ui-components/Editor/PrivacySettingsButton';
import Social from '../../../ui-components/Editor/Social';
import XsollaLogo from '../../../ui-components/Editor/XsollaLogo';
import { isSubsUsed } from '../../../utils/common-helper';
import { getSubscribersListButtonText } from '../../../utils/translation';
import ShareOptions from '../../ShareOptions';

import './style.pcss';

type Props = {
  block: FooterBlock,
  locale: FiveSymbolLocale,
  checkoutBlock: (blockId: string) => void,
  changeBlockById: ({
    blockId: string,
    propName: string,
    propValue: any
  }) => void,
  id: string,
  setAssets: setAssetType,
  saveBlockProcess: (
    block: FooterBlock
  ) => void,
  changeValue: (
    name: string,
    value: any,
    needSave: boolean
  ) => void,
  landing: Landing,
  allBlocks: Object[],
  localeList: FiveSymbolLocale[],
  widgets: WidgetsType,
  merchantId: string,
  renderMode: IRenderMode,
  isServerSideRender: boolean,
};

export class Footer extends Component<Props> {
  static isShowLocaleSelect(translationsLocation: $PropertyType<WidgetsType, 'translationsLocation'>, localeList: FiveSymbolLocale[]) {
    return ['footer', 'both'].includes(translationsLocation) && localeList.length > 1;
  }

  get localeForShare(): string {
    const { locale } = this.props;
    return locale !== 'zh-CN' && locale !== 'zh-TW' ? locale.substring(2, 0) : locale;
  }

  saveText = () => {
    const {
      block, id, changeBlockById, saveBlockProcess
    } = this.props;
    const { values } = block;
    const blockId = id;
    const propName = 'values';
    const propValue = { ...values };
    changeBlockById({ blockId, propName, propValue });
    saveBlockProcess(block);
  };

  onCheckoutBlock = () => {
    const { block, checkoutBlock } = this.props;
    checkoutBlock(block._id);
  };

  checkLinkExist = (url: string) => {
    if (url === '') {
      this.onCheckoutBlock();
    }
  };

  openAssetsLibrary = (assetFunction: Function, currentAsset: Asset | ImageValue) => () => {
    const { setAssets } = this.props;
    this.onCheckoutBlock();
    setAssets({ assetFunction, currentAsset });
  };

  setImage = (valueName: string) => (asset: Asset | ImageValue, needSave: boolean) => {
    const { changeValue } = this.props;
    changeValue(valueName, asset, needSave);
  };

  renderLogo = () => {
    const { block, renderMode, isServerSideRender } = this.props;
    const { logo, template } = block.values;
    return logo.enable && (logo.img || !isServerSideRender) && (
      <div className={cn(
        'footer__logo',
        `footer__logo--${template}-layout`
      )}
      >
        <Media
          asset={logo}
          onMediaButtonClick={this.openAssetsLibrary(this.setImage('logo'), logo)}
          renderMode={renderMode}
          isMainImage
        />
      </div>
    );
  };

  renderShareOptions = () => {
    const { widgets, block, isServerSideRender } = this.props;
    const { shareOptions } = widgets;
    const { template } = block.values;
    return shareOptions.enable && (
      <div
        className={cn(
          'footer__sharing-options ',
          `footer__sharing-options--${template}-layout`
        )}
      >
        <ShareOptions
          shareOptions={shareOptions.values}
          locale={this.localeForShare}
          isServerSideRender={isServerSideRender}
        />
      </div>
    );
  };

  renderSubscribersButton = () => {
    const { allBlocks, block, locale } = this.props;
    const { template } = block.values;

    // TODO копипаст с client/src/components/SideMenu/TopBar/index.jsx, нужно вынести в глобальное состояние
    let isSubscriptionSet = false;
    allBlocks.some(block => {
      const result = isSubsUsed(block.components);
      if (result.includesSubscriptionButton) {
        isSubscriptionSet = result.includesSubscriptionButton;
      }
      return isSubscriptionSet;
    });

    return isSubscriptionSet && (
      <Button
        className={cn(
          'footer__subscribers-button',
          `footer__subscribers-button--${template}-layout`
        )}
        customType='footer-subscribers-button'
        outline
      >
        {getSubscribersListButtonText(locale)}
      </Button>
    );
  };

  renderEditorText = () => {
    const { block, locale, isServerSideRender } = this.props;
    const { name, template } = block.values;
    return name.enable && (
      <EditorText
        className={cn(
          'footer__editor-text',
          `footer__editor-text--${template}-layout`
        )}
        size='small'
        text={name[locale]}
        locale={locale}
        values={block.values}
        onBlur={this.saveText}
        field='name'
        isServerSideRender={isServerSideRender}
        onCheckoutBlock={this.onCheckoutBlock}
        disableTextAlignSettings={template === 'three-columns'}
      />
    );
  };

  renderSocialLinks = (className: string) => {
    const { block } = this.props;
    const { template } = block.values;
    const socialComponent = block.components.find(component => component.type === 'social');
    return socialComponent && socialComponent.enable && (
      <div
        key={socialComponent._id}
        className={cn(
          `footer__social-links--${template}-layout`
        )}
      >
        <Social
          blockName='footer'
          checkLinkExist={this.checkLinkExist}
          socialComponent={socialComponent}
          className={className}
        />
      </div>
    );
  };

  renderAgeRating = () => {
    const { block } = this.props;
    const { template } = block.values;

    const ageRatingNames = ['ageRestriction', 'pegi', 'esrb', 'usk', 'classind', 'acb', 'oflc', 'iarc'];
    // $FlowFixMe
    const ageRatingIcons = block.components
      .filter(component => ageRatingNames.includes(component.type) && component.enable)
      .map(component => (
        <div
          key={component.type}
          className={cn(
            'footer__age-rating-icon',
            `footer__age-rating-icon--${component.value}`,
            `footer__age-rating-icon--${template}-layout`
          )}
        />
      ));

    return !!ageRatingIcons.length && (
      <div
        className={cn(
          'footer__age-rating',
          `footer__age-rating--${template}-layout`
        )}
      >
        {ageRatingIcons}
      </div>
    );
  }

  renderXsollaBadge = () => {
    const { locale } = this.props;

    return (<XsollaLogo locale={locale} />);
  };

  renderAffiliateButton = () => {
    const { landing, block, locale } = this.props;
    const { merchantId, projectId, analyticsCounterId } = landing;
    const { values } = block;
    return values.affiliate && merchantId && projectId && analyticsCounterId && (
      <AffiliateButton
        text={values.affiliate[locale]}
        linkInfo={{
          merchantId, projectId, landingId: analyticsCounterId
        }}
      />
    );
  };

  renderLocaleSelect = () => {
    const {
      widgets,
      localeList,
      locale,
      merchantId,
      renderMode
    } = this.props;
    const { translationsLocation } = widgets;
    return (Footer.isShowLocaleSelect(translationsLocation, localeList) && (
      // data attribute used for render LocaleSelect component in landing
      <div data-footer-locale-select>
        <LocaleSelect
          localeList={localeList}
          currentLocale={locale}
          merchantId={merchantId}
          renderMode={renderMode}
          type={LocaleSelectType.SLIM}
        />
      </div>
    ));
  };

  renderPrivacySettingsButton = () => {
    const { block, locale } = this.props;
    const { template } = block.values;
    return (
      <PrivacySettingsButton
        className={`footer__privacy-settings-button--${template}-layout`}
        locale={locale}
      />
    );
  }

  // если появятся новые раскладки, то вынести в client/src/layouts
  withThreeColumnsLayout = (
    children: {
      left: Array<?React$Node>,
      center: Array<?React$Node>,
      right: Array<?React$Node>
    }
  ) => (
    <div className='three-columns-layout'>
      {Object.entries(children).map((
        [position, columnChildren]: [string, any] // TODO https://github.com/facebook/flow/issues/5838
      ) => (
        <div
          key={position}
          className={cn(
            'three-columns-layout__column',
            `three-columns-layout__column--${position}`
          )}
        >
          {columnChildren
            .map((child, index) => {
              if (!child) {
                return null;
              }
              return (
                <div
                  // можем использовать индекс тк массивы созданные в renderThreeColumnsLayout методе неизменняемы
                  // eslint-disable-next-line react/no-array-index-key
                  key={index}
                  className={cn('three-columns-layout__column-item')}
                >
                  {child}
                </div>
              );
            })}
        </div>
      ))}
    </div>
  );

  renderThreeColumnsLayout = () => {
    const left = [
      this.renderLogo(),
      this.renderSocialLinks('center'),
      this.renderShareOptions()
    ];

    const center = [
      this.renderEditorText(),
      this.renderLocaleSelect(),
      this.renderSubscribersButton()
    ];

    const right = [
      this.renderXsollaBadge(),
      this.renderAffiliateButton(),
      this.renderAgeRating(),
      this.renderPrivacySettingsButton()
    ];

    return this.withThreeColumnsLayout({
      left, center, right
    });
  };

  renderOneColumnLayout = () => (
    <div className='one-column-layout'>
      <div className='one-column-layout__top-line'>
        {this.renderLogo()}
        {this.renderSocialLinks('left')}
        {this.renderShareOptions()}
        {this.renderEditorText()}
        {this.renderAgeRating()}
      </div>
      <div className='one-column-layout__bottom-line'>
        <div className='one-column-layout__bottom-left'>
          {this.renderXsollaBadge()}
          {this.renderAffiliateButton()}
        </div>
        <div className='one-column-layout__bottom-right'>
          {this.renderLocaleSelect()}
          {this.renderSubscribersButton()}
        </div>
      </div>
      {this.renderPrivacySettingsButton()}
    </div>
  );

  render() {
    const { block, renderMode } = this.props;
    const { background, template } = block.values;
    return (
      <>
        <Media
          className='custom-background'
          asset={background}
          onMediaButtonClick={this.onCheckoutBlock}
          isMainImage={false}
          renderMode={renderMode}
          showCustomBackground={background.enable}
          mediaType='background'
        />
        <footer className='footer block-content'>
          {template === 'three-columns' && this.renderThreeColumnsLayout()}
          {template === 'one-column' && this.renderOneColumnLayout()}
        </footer>
      </>
    );
  }
}

const connector: Function = connect(
  ({ blocks, landing }: RootState, ownProps: Props) => ({
    allBlocks: blocks.blocks,
    block: blocks.blocks.find(block => block._id === ownProps.id),
    landing: landing.landing,
    locale: landing.locale,
    localeList: landing.landing.languages,
    widgets: landing.landing.widgets,
    merchantId: selectMerchantId(landing),
    renderMode: landing.renderMode
  }), {
    checkoutBlock, changeBlockById, setAssets, saveBlockProcess, changeValue
  }
);

export default connector(Footer);
